/**
 * The ticket form's Link button: pick a ticket-type template for a NEW ticket
 * that will be linked to the one being edited.
 *
 * One dialog, not two: when the source ticket has unsaved edits the footer
 * becomes Save & continue / Discard & continue instead of a plain Continue,
 * so the "you'll lose your changes" question is asked alongside the choice
 * that would cause it rather than as a second modal on top.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { GroupedTicketTypePicker } from './GroupedTicketTypePicker';
import type { TicketTypeTemplate } from '../../types/ticketTypeTemplate';
import './LinkedTicketDialog.css';

export type LinkedTicketContinueMode = 'continue' | 'save' | 'discard';

interface LinkedTicketDialogProps {
  open: boolean;
  sourceNumber?: number | string | null;
  customerName?: string;
  templates: TicketTypeTemplate[];
  isLoadingTemplates?: boolean;
  hasUnsavedChanges: boolean;
  /** True while a "Save & continue" save is in flight. */
  isSaving?: boolean;
  /** Why the form can't be saved right now (the form's own canSave rule);
   *  disables "Save & continue" without hiding it. */
  saveBlockedReason?: string;
  onCancel: () => void;
  onContinue: (template: TicketTypeTemplate, mode: LinkedTicketContinueMode) => void;
}

export const LinkedTicketDialog: React.FC<LinkedTicketDialogProps> = ({
  open,
  sourceNumber,
  customerName,
  templates,
  isLoadingTemplates,
  hasUnsavedChanges,
  isSaving,
  saveBlockedReason,
  onCancel,
  onContinue,
}) => {
  const [template, setTemplate] = useState<TicketTypeTemplate | null>(null);

  useEffect(() => {
    if (open) setTemplate(null);
  }, [open]);

  const ref = sourceNumber ? `#${sourceNumber}` : 'this ticket';

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'linked-ticket-dialog' }}
    >
      <DialogTitle>New linked ticket</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogContentText>
          Choose a ticket template. A new ticket{customerName ? ` for ${customerName}` : ''} opens
          pre-filled from it and linked to {ref}.
        </DialogContentText>
        <GroupedTicketTypePicker
          value={template}
          onChange={setTemplate}
          templates={templates}
          isLoading={isLoadingTemplates}
          disabled={isSaving}
          label="Ticket template"
        />
        {hasUnsavedChanges && (
          <Alert severity="warning">
            {ref} has unsaved changes. Save them first, or discard them and continue.
            {saveBlockedReason ? ` It can't be saved yet: ${saveBlockedReason}.` : ''}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
        {hasUnsavedChanges ? (
          <>
            <Button
              color="warning"
              disabled={!template || isSaving}
              onClick={() => template && onContinue(template, 'discard')}
            >
              Discard &amp; continue
            </Button>
            <Button
              variant="contained"
              disabled={!template || isSaving || !!saveBlockedReason}
              startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
              onClick={() => template && onContinue(template, 'save')}
            >
              Save &amp; continue
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            disabled={!template}
            onClick={() => template && onContinue(template, 'continue')}
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
